import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, UserPlus } from 'lucide-react'
import type { Agent } from '@/api/directoryApi'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

type Person = { id: string; name: string; initials: string }
type Size = 'sm' | 'md'

const SIZE: Record<Size, string> = { sm: 'size-5 text-[9px]', md: 'size-6 text-[10px]' }

/** A person's initials in a circle; the card face and the picker draw the same one. */
export function AssigneeAvatar({ person, size = 'md', className }: { person: Person; size?: Size; className?: string }) {
  return (
    <span title={person.name} className={cn('flex shrink-0 items-center justify-center rounded-full bg-primary font-semibold text-primary-foreground', SIZE[size], className)}>
      {person.initials}
    </span>
  )
}

/** Me, Unassign and a searchable list of agents; shared by the avatar picker and the card's "…" menu. */
export function AssigneeOptions({ assigneeId, agents, meId, onPick }: { assigneeId: string | null; agents: Agent[]; meId: string | null; onPick: (id: string | null) => void }) {
  const { t } = useTranslation()
  const [who, setWho] = useState('')
  const q = who.trim().toLowerCase()
  const people = agents.filter((a) => a.id !== meId && a.name.toLowerCase().includes(q))
  const item = 'flex w-full items-center gap-2 rounded-sm px-2.5 py-1.5 text-left text-meta text-ink-body hover:bg-surface-subtle focus-visible:bg-surface-subtle focus-visible:outline-none'
  const me = meId ? agents.find((a) => a.id === meId) : undefined

  return (
    <>
      {meId && assigneeId !== meId ? (
        <button type="button" className={cn(item, 'font-medium text-primary')} onClick={() => onPick(meId)}>
          {me ? <AssigneeAvatar person={me} size="sm" /> : null}
          {t('boards.card.me')}
        </button>
      ) : null}
      {assigneeId ? (
        <button type="button" className={item} onClick={() => onPick(null)}>
          <span className="flex size-5 shrink-0 items-center justify-center rounded-full border border-dashed border-line-strong" aria-hidden />
          {t('boards.card.unassign')}
        </button>
      ) : null}
      <input
        aria-label={t('boards.card.findPerson')}
        placeholder={t('boards.card.findPerson')}
        value={who}
        onChange={(e) => setWho(e.target.value)}
        className="mx-1 my-1 w-[calc(100%-0.5rem)] rounded-md border border-line-strong px-2 py-1 text-meta focus-visible:border-primary focus-visible:outline-none"
      />
      {people.slice(0, 12).map((a) => (
        <button key={a.id} type="button" className={item} aria-pressed={a.id === assigneeId} onClick={() => onPick(a.id)}>
          <AssigneeAvatar person={a} size="sm" />
          <span className="min-w-0 grow truncate">{a.name}</span>
          {a.id === assigneeId ? <Check className="size-3.5 shrink-0 text-primary" aria-hidden /> : null}
        </button>
      ))}
      {people.length === 0 ? <p className="px-2.5 py-1.5 text-meta text-ink-muted">{t('boards.card.noPeople')}</p> : null}
    </>
  )
}

/**
 * The assignee avatar as a control: hover names who has the ticket, click
 * opens the people list. Unassigned shows a dashed add-person circle. Without
 * the assign permission it is just the avatar.
 */
export function AssigneePicker({
  ticketKey,
  assignee,
  agents,
  meId,
  canAssign,
  size = 'md',
  showName = false,
  onChange,
}: {
  ticketKey: string
  assignee: Person | null
  agents: Agent[]
  meId: string | null
  canAssign: boolean
  size?: Size
  showName?: boolean
  onChange: (id: string | null) => void
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const name = showName ? <span className="min-w-0 truncate">{assignee?.name ?? t('boards.unassigned')}</span> : null
  const face = assignee ? (
    <AssigneeAvatar person={assignee} size={size} />
  ) : (
    <span className={cn('flex shrink-0 items-center justify-center rounded-full border border-dashed border-line-strong bg-background text-ink-muted', SIZE[size])}>
      <UserPlus className="size-3" aria-hidden />
    </span>
  )

  if (!canAssign) {
    if (!assignee && !showName) return null
    return (
      <span className="flex min-w-0 items-center gap-2">
        {assignee ? face : null}
        {name}
      </span>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={assignee ? t('boards.card.assignedTo', { name: assignee.name }) : t('boards.card.assignKey', { key: ticketKey })}
          aria-label={assignee ? t('boards.card.changeAssignee', { key: ticketKey, name: assignee.name }) : t('boards.card.assignKey', { key: ticketKey })}
          className="flex min-w-0 items-center gap-2 rounded-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&>span:first-child]:transition-shadow hover:[&>span:first-child]:ring-2 hover:[&>span:first-child]:ring-primary/30"
        >
          {face}
          {name}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="max-h-80 w-60 overflow-y-auto p-1">
        <p className="px-2.5 pb-1 pt-2 text-eyebrow font-medium uppercase text-ink-muted">{t('boards.card.assign')}</p>
        <AssigneeOptions
          assigneeId={assignee?.id ?? null}
          agents={agents}
          meId={meId}
          onPick={(id) => {
            setOpen(false)
            if (id !== (assignee?.id ?? null)) onChange(id)
          }}
        />
      </PopoverContent>
    </Popover>
  )
}
